// src/components/shop/CartDropdown.js

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { T, F, formatKsh } from "../../config/shop";

export default function CartDropdown() {
  const navigate = useNavigate();
  const {
    items,
    isOpen,
    setIsOpen,
    removeFromCart,
    updateQty,
    totalItems,
    totalPrice,
  } = useCart();

  const [notice, setNotice] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) setNotice("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, setIsOpen]);

  const go = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const changeQty = (item, nextQty) => {
    const result = updateQty(item.cartItemId, nextQty);
    setNotice(result.ok ? "" : result.message);
  };

  return (
    <>
      <style>{`
        @keyframes kk_dropIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .kk-cart-scroll::-webkit-scrollbar { width: 4px; }
        .kk-cart-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,.2); border-radius: 4px; }
        .kk-qty-btn:hover:not(:disabled) { background: ${T.yellow} !important; }
        .kk-link-btn:hover { text-decoration: underline; }
        .kk-checkout-btn:hover { background: ${T.yellowDark} !important; }
        .kk-view-btn:hover { background: ${T.surface} !important; }
        .kk-cart-trigger:focus-visible { outline: 2px solid ${T.yellow}; outline-offset: 4px; }
      `}</style>

      <div ref={ref} style={{ position: "relative" }}>
        <button
          type="button"
          className="kk-cart-trigger"
          onClick={() => setIsOpen((o) => !o)}
          aria-label={`Cart, ${totalItems} ${totalItems === 1 ? "item" : "items"}`}
          aria-expanded={isOpen}
          style={{
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            color: T.white,
            background: "none",
            border: "none",
            padding: 0,
            fontFamily: F.body,
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <span style={{ position: "relative", display: "flex" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            {totalItems > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -7,
                  right: -10,
                  background: T.yellow,
                  color: T.black,
                  borderRadius: 9,
                  minWidth: 17,
                  height: 17,
                  padding: "0 4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}
          </span>
          <span style={{ fontSize: 10.5, color: "rgba(255,255,255,.85)" }}>Cart</span>
        </button>

        {isOpen && (
          <div
            role="dialog"
            aria-label="Your cart"
            style={{
              position: "absolute",
              top: "calc(100% + 14px)",
              right: 0,
              width: 380,
              maxWidth: "calc(100vw - 16px)",
              background: T.white,
              border: `1px solid ${T.border}`,
              boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
              borderRadius: 6,
              overflow: "hidden",
              zIndex: 600,
              animation: "kk_dropIn .16s ease forwards",
              fontFamily: F.body,
              color: T.ink,
            }}
          >
            <div
              style={{
                background: T.black,
                borderBottom: `3px solid ${T.yellow}`,
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ color: T.white, fontWeight: 800, fontSize: 14 }}>Your cart</div>
                <div style={{ color: "rgba(255,255,255,.6)", fontSize: 11.5, marginTop: 2 }}>
                  {totalItems} {totalItems === 1 ? "item" : "items"}
                </div>
              </div>
              <button
                type="button"
                aria-label="Close cart"
                onClick={() => setIsOpen(false)}
                style={{
                  background: "rgba(255,255,255,.12)",
                  border: "none",
                  color: T.white,
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: 13,
                  fontFamily: "inherit",
                }}
              >
                ✕
              </button>
            </div>

            {items.length === 0 ? (
              <div style={{ padding: "34px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Your cart is empty</div>
                <div style={{ fontSize: 12.5, color: T.muted, lineHeight: 1.6, marginBottom: 16 }}>
                  Add a handmade piece and it will show up here.
                </div>
                <button
                  type="button"
                  onClick={() => go("/")}
                  style={{
                    padding: "10px 22px",
                    background: T.yellow,
                    color: T.black,
                    border: "none",
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Browse products
                </button>
              </div>
            ) : (
              <>
                <div className="kk-cart-scroll" style={{ maxHeight: 340, overflowY: "auto" }}>
                  {items.map((item, idx) => (
                    <CartRow
                      key={item.cartItemId}
                      item={item}
                      isLast={idx === items.length - 1}
                      onOpen={() => go(`/product/${item.id}`)}
                      onQty={(q) => changeQty(item, q)}
                      onRemove={() => removeFromCart(item.cartItemId)}
                    />
                  ))}
                </div>

                {notice && (
                  <div
                    role="status"
                    style={{
                      padding: "8px 16px",
                      background: T.yellow,
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {notice}
                  </div>
                )}

                <div
                  style={{
                    padding: "12px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    borderTop: `1px solid ${T.border}`,
                  }}
                >
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>Subtotal</span>
                  <span style={{ fontSize: 17, fontWeight: 800 }}>{formatKsh(totalPrice)}</span>
                </div>

                <div style={{ padding: "0 16px 16px", display: "grid", gap: 8 }}>
                  <button
                    type="button"
                    className="kk-checkout-btn"
                    onClick={() => go("/checkout")}
                    style={{
                      width: "100%",
                      minHeight: 46,
                      background: T.yellow,
                      color: T.black,
                      border: `2px solid ${T.black}`,
                      borderRadius: 2,
                      fontWeight: 800,
                      fontSize: 14,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Checkout
                  </button>
                  <button
                    type="button"
                    className="kk-view-btn"
                    onClick={() => go("/cart")}
                    style={{
                      width: "100%",
                      minHeight: 42,
                      background: T.white,
                      color: T.black,
                      border: `1.5px solid ${T.border}`,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    View full cart
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function CartRow({ item, isLast, onOpen, onQty, onRemove }) {
  const variant = [item.selectedColor, item.selectedSize].filter(Boolean).join(" · ");
  const atMax = item.stock !== null && item.qty >= item.stock;

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "12px 16px",
        borderBottom: isLast ? "none" : `1px solid ${T.borderSoft}`,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${item.title}`}
        style={{
          width: 64,
          height: 64,
          flexShrink: 0,
          padding: 0,
          border: `1px solid ${T.borderSoft}`,
          borderRadius: 6,
          background: "#fafaf8",
          overflow: "hidden",
          cursor: "pointer",
        }}
      >
        {item.image_url ? (
          <img src={item.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        ) : (
          <span style={{ fontSize: 11, color: T.muted }}>No image</span>
        )}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <button
          type="button"
          className="kk-link-btn"
          onClick={onOpen}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textAlign: "left",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.35,
            color: T.ink,
          }}
        >
          {item.title}
        </button>

        {variant && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{variant}</div>}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", border: `1px solid ${T.border}`, borderRadius: 999, overflow: "hidden" }}>
            <QtyButton label="Decrease quantity" onClick={() => onQty(item.qty - 1)} disabled={item.qty <= 1}>−</QtyButton>
            <span style={{ minWidth: 26, textAlign: "center", fontSize: 12.5, fontWeight: 700 }}>{item.qty}</span>
            <QtyButton label="Increase quantity" onClick={() => onQty(item.qty + 1)} disabled={atMax}>+</QtyButton>
          </div>
          <span style={{ fontSize: 13.5, fontWeight: 800 }}>{formatKsh(item.price * item.qty)}</span>
        </div>

        <button
          type="button"
          className="kk-link-btn"
          onClick={onRemove}
          style={{
            marginTop: 6,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 11.5,
            fontWeight: 700,
            color: T.error,
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function QtyButton({ children, label, onClick, disabled }) {
  return (
    <button
      type="button"
      className="kk-qty-btn"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 28,
        height: 26,
        border: "none",
        background: T.white,
        fontSize: 15,
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1,
      }}
    >
      {children}
    </button>
  );
}